/**
 * Ryan O'Dowd
 * 2022-08-03
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  FEEDBACK_CHAT_ROUTE,
  fetchParticipantCampaigns,
  joinFundraisingCampaign,
} from '@utils/store/actions';
import {
  Linking,
  View,
} from 'react-native';
import React, {
  useEffect,
  useState,
} from 'react';
import Button from '@components/common/Button';
import Campaign from './Campaign';
import Header from '@components/Header';
import {
  KeyboardAwareScrollView,
} from 'react-native-keyboard-controller';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import hooks from '@hooks';

const CODE_LENGTH = 7;

const Fundraising = () => { // @TODO: rename to something like NewEditGoal?
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => { // @TODO: can this be part of Header.BackIcon?
    return false;
  });

  const [_invitationCodeText, setInvitationCodeText] = useState('');
  const [_joinMessage, setJoinMessage] = useState('');
  const [_joinIsError, setJoinIsError] = useState(false);
  const participantCampaigns = hooks.useTypedSelector((state) => state.participantCampaigns.filter((campaign) => new Date(campaign.end_datetime).getTime() >= new Date().getTime()), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  useEffect(() => {
    dispatch(fetchParticipantCampaigns());
  }, [dispatch]);

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        title={i18n('fundraising')}
        leftItems={[backIcon]}
      />
      {/* @TODO: this should look a little prettier...why is there no padding underneath the textinput? */}
      <KeyboardAwareScrollView
        contentContainerStyle={styles.list}
        style={styles.innerContainer}
        bottomOffset={32}
      >
        {/* @TODO: i thought using NestableScrollContainer was supposed to fix `VirtualizedLists should never be nested inside plain ScrollViews with the same orientation because it can break windowing and other functionality` issue...why the conosle warning? */}
        <View style={styles.descriptionWrapper}>
          <Text style={styles.descriptionText}>{i18n('wrinklyPartnersWith')}</Text>
          <Text style={styles.descriptionText}>{i18n('wrinklyFundraisingMoreInformation')} <Text style={styles.link} onPress={() => Linking.openURL('https://wrink.ly/fundraising')}>{i18n('ourWebsite')}</Text>.</Text>
        </View>

        {!!participantCampaigns.length &&
          <View style={styles.formSection}>
            <Text style={styles.sectionHeaderText}>{i18n('myCampaigns')}</Text>
            <View style={styles.campaigns}>
              {participantCampaigns.map((campaign) => {
                return <Campaign key={campaign.id} {...campaign} />;
              })}{/* @TODO: remove filter to show users "view past campaigns" */}
            </View>
          </View>
        }

        <View style={styles.formSection}>
          <Text style={styles.sectionHeaderText}>{i18n('join')}</Text>
          <View style={styles.formSectionInner}>
            <Text style={styles.descriptionText}>{i18n('joinBody')}</Text>
            <View style={styles.joinWrapper}>
              <View style={styles.textInputWrapper}>
                <TextInput
                  style={styles.titleTextInput}
                  maxLength={CODE_LENGTH}
                  autoFocus={false}
                  numberOfLines={1}
                  textColor={styles.colors.white}
                  autoCapitalize='characters'
                  autoCorrect={false}
                  onChangeText={(invitationCodeText) => setInvitationCodeText(invitationCodeText.toUpperCase())}
                  value={_invitationCodeText}
                  mode='outlined'
                  outlineColor={styles.colors.primaryExtraDark}
                  activeOutlineColor={styles.colors.accent}
                  placeholder={i18n('code')}
                />
              </View>
              <Button
                theme={styles.colors.accent}
                disabled={_invitationCodeText.length < CODE_LENGTH}
                onPress={() => {
                  // save to back end
                  dispatch(joinFundraisingCampaign(_invitationCodeText, (isError, message) => {
                    if (!isError) {
                      setInvitationCodeText('');
                    }
                    setJoinIsError(isError);
                    setJoinMessage(message);
                  }));
                }}
              >
                {i18n('joinCampaign')}
              </Button>
            </View>
            <View style={styles.messageWrapper}>
              <Text style={[styles.joinMessage, _joinIsError && styles.errorText]}>{_joinMessage}</Text>
            </View>
          </View>
        </View>

        <View style={styles.organizationInfoWrapper}>
          <Text style={styles.descriptionText}>{i18n('interestedInJoining')} <Text style={styles.link} onPress={() => navigation.navigate(FEEDBACK_CHAT_ROUTE)}>{i18n('sendUsAMessage')}</Text>{i18n('weCanSetUpAccount')}</Text>
        </View>
      </KeyboardAwareScrollView>
    </ScreenWrapper>
  );
};

export default Fundraising;
